import React from 'react';
import { Badge, BadgeVariant } from '@/components/ui/Badge';
import { HealthStatus } from '@/types/health';

/**
 * 사용자 상태에 따른 Badge 컴포넌트 반환
 */
export const getUserStatusBadge = (status: string): React.ReactNode => {
  const statusMap: Record<string, { label: string; variant: BadgeVariant }> = {
    active: { label: '활성화', variant: 'active' },
    pending: { label: '승인 대기', variant: 'pending' },
    invited: { label: '초대됨', variant: 'invited' },
    deactivated: { label: '비활성화', variant: 'pending' },
    deleted: { label: '삭제됨', variant: 'deleted' },
  };

  const statusInfo = statusMap[status] || { label: status, variant: 'default' as BadgeVariant };
  return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
};

/**
 * 서비스 상태에 따른 Badge 컴포넌트 반환
 */
export const getServiceStatusBadge = (status: string | undefined): React.ReactNode => {
  if (!status) {
    return <Badge variant="default">미정</Badge>;
  }

  const statusMap: Record<string, { label: string; variant: BadgeVariant }> = {
    pending: { label: '등록대기중', variant: 'warning' },
    approved: { label: '등록됨', variant: 'success' },
    rejected: { label: '거부됨', variant: 'error' },
    suspended: { label: '일시중지', variant: 'warning' },
    deleted: { label: '삭제됨', variant: 'default' },
  };

  const statusInfo = statusMap[status] || { label: status, variant: 'default' as BadgeVariant };
  return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
};

/**
 * 헬스 상태에 따른 Badge 컴포넌트 반환
 */
export const getHealthBadge = (
  healthStatus: HealthStatus | undefined
): React.ReactNode => {
  if (!healthStatus) {
    return <Badge variant="default">확인중</Badge>;
  }
  // 승인 전 서비스는 서버가 확인하지 않는다(신청 주소로 바깥 요청을 보내지 않는다, #304)
  if (healthStatus === 'PENDING') {
    return <Badge variant="default">승인 전</Badge>;
  }
  return (
    <Badge variant={healthStatus === 'ONLINE' ? 'success' : 'error'}>
      {healthStatus}
    </Badge>
  );
};

/**
 * 인증 유형에 따른 Badge 컴포넌트 반환
 */
export const getAuthTypeBadge = (authType: string | undefined): React.ReactNode => {
  const authTypeMap: Record<string, { label: string; variant: BadgeVariant }> = {
    password: { label: '🔑 ID/PW', variant: 'default' },
    oauth: { label: '🔗 Google', variant: 'info' },
    pending_oauth: { label: '⏳ 전환대기', variant: 'warning' },
  };

  const authInfo = authTypeMap[authType || 'password'] || { label: authType || '-', variant: 'default' as BadgeVariant };
  return <Badge variant={authInfo.variant}>{authInfo.label}</Badge>;
};

